import { HttpHeaders } from '@angular/common/http'
import { Injectable, computed, signal } from '@angular/core'

/**
 * Tracks the backend's rate-limit budget, read from the response headers the
 * API returns on every call:
 *   - x-ratelimit-limit:     max requests allowed in the current window
 *   - x-ratelimit-remaining: requests left in the current window
 *   - x-ratelimit-reset:     seconds until the window resets
 *
 * This is the single source of truth the interceptor writes to and the rest of
 * the app reads from. It does not throttle anything itself — see
 * rateLimit.interceptor.ts (hard gate + 429 backoff) and the concurrency utils.
 */
@Injectable({ providedIn: 'root' })
export class RateLimitService {
  /** Max requests per window. Infinity until the first response is seen. */
  readonly limit = signal(Infinity)
  /** Requests left in the current window. Infinity until first response. */
  readonly remaining = signal(Infinity)
  /** Epoch ms at which the current window resets (0 = unknown). */
  readonly resetAt = signal(0)

  /** Budget is running low — widen debounces, ease off fan-out. */
  readonly isLow = computed(() => this.remaining() <= 5)
  /** Budget is spent — the gate holds requests until the window resets. */
  readonly isExhausted = computed(() => this.remaining() <= 0)

  /** Fold a response's (or error's) rate-limit headers into the signals. */
  update(headers: HttpHeaders): void {
    const limit = headers.get('x-ratelimit-limit')
    const remaining = headers.get('x-ratelimit-remaining')
    const reset = headers.get('x-ratelimit-reset')

    if (limit != null) this.limit.set(+limit)
    if (remaining != null) this.remaining.set(+remaining)
    if (reset != null) this.resetAt.set(Date.now() + +reset * 1000)
  }

  /** Milliseconds until the window resets, never negative. */
  msUntilReset(): number {
    return Math.max(0, this.resetAt() - Date.now())
  }
}
